import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { getDisabledDates } from "../state_management/epics";
import { orderType } from "../state_management/reducers/order";
import { useAsync } from "./asyncFunc";
import { validDate } from "./misc";
import { cleaner, workSchedule } from "../state_management/actionTypes";
import {
  extractTimeDetails,
  getMinsTotal,
  getNextAvailableDay,
  getTimeStartInfo,
  prefixTransform
} from "./cleanerTime";
import {
  c_item,
  checkAddress,
  checkAddressParams,
  disabledDateParams,
  e_item,
  getCleanerTimeParams,
  get_cleaner_time,
  parseIdQuan
} from "../state_management/epics/order";
import moment from "moment";
import { useTimeout } from "./useTimeout";
import { LocaleContext } from "../AppProvider";
import { useSentry } from "./sentry";

const getDate = (date: string | undefined): Date => {
  try {
    if (date) {
      // Replace - with / to force local timezone parsing (e.g. 2026/07/28) instead of UTC midnight
      const localDateStr = date.replace(/-/g, "/");
      const newDate = new Date(localDateStr);

      if (!validDate(newDate)) {
        throw Error(`Invalid date!`);
      }

      return newDate;
    } else {
      return new Date();
    }
  } catch (err) {
    return new Date();
  }
};

const extractTimeStartDetails = (
  time: string | null,
  checkout: boolean | undefined
) => {
  const dateString = getTimeStartInfo(time, true).date;
  const dateSelected = dateString ? getDate(dateString) : undefined;

  if (time) {
    return {
      date: time ? dateSelected : undefined,
      month: dateSelected
    };
  } else {
    /** We want to set a default for direct checkout
     * This is because in direct checkout, we do not eventually set the month from an eventually fetched month
     * Unlike in reschedule's case where the month is eventually fetched from the endpoint and will never be empty
     */
    return { date: undefined, month: checkout ? dateSelected : undefined };
  }
};

const sameDates = (...dates: (Date | undefined)[]) => {
  try {
    if (!dates.length || dates.some((date) => !date)) return false;
    const firstDateTime = new Date(dates[0]!).getTime();
    return dates.every(
      (date) => date && new Date(date).getTime() === firstDateTime
    );
  } catch (err) {
    return false;
  }
};

export const useTimeDetails = ({
  longitude,
  latitude,
  eircode,
  address,
  orderDetails,
  services,
  extra_services,
  setOrderDetails,
  setAddressBlacklisted,
  hide,
  checkout
}: {
  longitude: number | undefined;
  latitude: number | undefined;
  eircode: string | undefined;
  address: string;
  orderDetails: orderType;
  services: (c_item & { duration: number })[];
  extra_services: (e_item & { duration: number })[];
} & (
  | {
      hide: boolean;
      checkout: true;
      setOrderDetails: (details: Partial<orderType>) => void;
      setAddressBlacklisted: (data: boolean) => void;
    }
  | {
      hide: false;
      checkout?: never;
      setOrderDetails?: never;
      setAddressBlacklisted?: never;
    }
)) => {
  const { t } = useContext(LocaleContext);

  const [date, setDate] = useState(
    extractTimeStartDetails(orderDetails.time_start, checkout).date
  );
  const [month, setMonth] = useState(
    extractTimeStartDetails(orderDetails.time_start, checkout).month
  );
  const [disabledDates, setDisabledDates] = useState<string[]>();
  const [disabledDateMonth, setDisabledDateMonth] = useState<number>();
  const [cleaners, setCleaners] = useState<cleaner[]>();
  const [timeArray, setTimeArray] = useState<workSchedule[]>();
  const [cleanerError, setCleanerError] = useState(false);
  const [timeDetailsError, setTimeDetailsError] = useState(false);
  const [noCleanerLocationError, setNoCleanerLocationError] = useState(false);
  const [monthMovementCount, setMonthMovementCount] = useState(0);

  const Sentry = useSentry();

  // Always keep a live reference to orderDetails to avoid stale closures
  const orderDetailsRef = useRef(orderDetails);
  orderDetailsRef.current = orderDetails;

  const cartCombined = useMemo(() => {
    return {
      services: services.map((item) => parseIdQuan({ item, extra: false })),
      extra_services: extra_services.map((item) =>
        parseIdQuan({ item, extra: true })
      )
    };
  }, [
    extra_services.map((i) => `${i.id}-${i.quantity}`).join("|"),
    services.map((i) => `${i.id}-${i.quantity}`).join("|")
  ]);

  const serviceDuration = (() => {
    const getDuration = (duration: number) => {
      const isNum = !isNaN(parseFloat(`${duration}`));

      return isNum ? duration : 0;
    };
    const durationArray = services.map(
      (item) => getDuration(item.duration) * item.quantity
    );

    const extraDurationArray = extra_services.map(
      (item) => getDuration(item.duration) * item.quantity
    );

    const durationTotal = [...durationArray, ...extraDurationArray].reduce(
      (a, b) => a + b,
      0
    );
    return durationTotal;
  })();

  useEffect(() => {
    if (!address) {
      setDate(undefined);
    }
  }, [address, setDate]);

  useEffect(() => {
    const timeStart = orderDetails.time_start;

    // This fixing is necessary because orderDetails is usually fetched from
    if (timeStart && checkout) {
      const { date: timeStartDate, month: timeStartMonth } =
        extractTimeStartDetails(timeStart, checkout);
      if (!date && timeStartDate && timeStartMonth) setDate(timeStartDate);
      if (!month && timeStartMonth) setMonth(timeStartMonth);
    }
  }, [orderDetails.time_start]);



  const hydratedMonthForDisabled = extractTimeStartDetails(orderDetailsRef.current.time_start, checkout).month;

  const disabledDatesRes = useAsync(
    {
      asyncFunc: getDisabledDates,
      funcParams: {
        ...cartCombined,
        date: month || hydratedMonthForDisabled,
        latitude,
        longitude,
        cleaner_id: orderDetails.cleaner_id || undefined
      } as disabledDateParams,
      immediate: true,
      clearOnError: true,
      id: "check"
    },
    [cartCombined, month, latitude, longitude, orderDetails.cleaner_id, address]
  );

  useEffect(() => {
    if (disabledDatesRes.error && disabledDatesRes.errorObject) {
      const type = (disabledDatesRes.errorObject as { type: any }).type;
      if (type === "no_free_time") {
        setTimeDetailsError(true);
      } else if (type === "not_valid_location") {
        setNoCleanerLocationError(true);
      }
    } else {
      setTimeDetailsError(false);
      setNoCleanerLocationError(false);
    }
  }, [disabledDatesRes.error, disabledDatesRes.errorObject]);

  /**
   * You can only fetch the available times if
   * - The address has been checked
   * - The disabled dates results has been retrieved
   * - The current date is not disabled
   */
  const isSelectedDateEnabled = useMemo((): boolean => {
    const effectiveDate = date || extractTimeStartDetails(orderDetailsRef.current.time_start, checkout).date;
    if (disabledDatesRes.data && effectiveDate) {
      const isSameMonth =
        effectiveDate.getMonth() === disabledDatesRes.params.date?.getMonth();
      const disabledDays = disabledDatesRes.data.disabled_days;
      const monthDate = effectiveDate.getDate();

      if (isSameMonth)
        return disabledDays.every((d) => Number(d) !== monthDate);
      return false;
    } else {
      return false;
    }
  }, [date, orderDetails.time_start, disabledDatesRes.data, disabledDatesRes.params.date]);

  /**
   * I can check the address when I have the the disabled dates info ready
   * And also if there is a selected date that is not disabled
   */
  const hasFetchedDisabedDates = Boolean(
    checkout &&
    !!disabledDatesRes.data &&
    !disabledDatesRes.loading &&
    isSelectedDateEnabled
  );

  const addressBlackListCheckRes = useAsync(
    {
      asyncFunc: checkAddress,
      funcParams: {
        date: date || extractTimeStartDetails(orderDetailsRef.current.time_start, checkout).date,
        address,
        latitude,
        longitude,
        cleaner_id: orderDetails.cleaner_id || undefined,
        ...cartCombined
      } as checkAddressParams,
      canCall: hasFetchedDisabedDates,
      immediate: true,
      clearOnError: true
    },
    [cartCombined, address, latitude, longitude, date, hasFetchedDisabedDates, orderDetails.cleaner_id, orderDetails.time_start]
  );



  const hydratedDateForTime = extractTimeStartDetails(orderDetailsRef.current.time_start, checkout).date;

  const availableTimeRes = useAsync(
    {
      asyncFunc: get_cleaner_time,
      funcParams: {
        ...cartCombined,
        date: date || hydratedDateForTime,
        latitude,
        longitude,
        random_cleaner: !!orderDetails.random_cleaner,
        cleanerId: orderDetails.cleaner_id
      } as getCleanerTimeParams,
      immediate: true,
      clearOnError: true
    },
    [cartCombined, date, longitude, latitude, orderDetails.cleaner_id]
  );

  useEffect(() => {
    const cleanerTimes = availableTimeRes.data || [];
    const availableTimesFetchDone =
      availableTimeRes.called && !availableTimeRes.loading;
    // This is to ensure that time is updated according to the date and cleaner clicked
    // In case of a slow network and multiple date clicks
    const sameCleaner =
      orderDetails.cleaner_id === availableTimeRes.params?.cleanerId;
    // Use effectiveDate: local date state OR the date derived from the hydrated time_start
    const effectiveDate = date || extractTimeStartDetails(orderDetailsRef.current.time_start, checkout).date;
    const sameDate = sameDates(effectiveDate, availableTimeRes.params?.date);
    const sameLocation =
      orderDetails.longitude === availableTimeRes.params.longitude &&
      orderDetails.latitude === availableTimeRes.params.latitude;
    const hasSameParams = sameDate && sameCleaner && sameLocation;

    if (hasSameParams) {
      const intervals = cleanerTimes.map(({ available, time }) => ({
        free: available,
        time: getMinsTotal(extractTimeDetails(time))
      }));

      setTimeArray(intervals);

      // This is to ensure the time_start conform when timeArray changes
      if (availableTimesFetchDone) {
        const timeStart = getTimeStartInfo(orderDetailsRef.current.time_start).time || null;
        if (timeStart) {
          const { hrs, mins } = extractTimeDetails(timeStart);
          const startMinTime = hrs * 60 + mins;

          let exists = intervals.some((timeObj) => {
            return timeObj.time === startMinTime;
          });

          if (!exists && checkout && !hide && setOrderDetails) {
            // If previous selection does not exist, remove the previous selection
            // Only wipe time_start when the Time step is actually visible (not hidden)
            // Use orderDetailsRef.current to avoid stale closure stripping cleaner_id - critical bug
            setOrderDetails({
              ...orderDetailsRef.current,
              time_start: null
            });
          }
        }
      }

      // Report if the date and address exists but with no intervals
      if (
        checkout &&
        availableTimesFetchDone &&
        (date || orderDetailsRef.current.time_start) &&
        address &&
        intervals.every(({ free }) => !free)
      ) {
        Sentry.captureMessage("All time cells are disabled", {
          level: "info",
          extra: {
            ...availableTimeRes.params,
            address,
            date
          }
        });
      }
    }
  }, [
    date,
    address,
    checkout,
    availableTimeRes.data,
    availableTimeRes.called,
    availableTimeRes.loading,
    availableTimeRes.params,
    orderDetails.cleaner_id,
    orderDetails.latitude,
    orderDetails.longitude,
    orderDetails.time_start
  ]);

  /**
   * We are using this custom setTimeout hook
   * ...because we do not want to flash error msg
   * Showing error should be shown after X amount of milliseconds into making the request
   */
  useTimeout(
    {
      func: () => {
        if (availableTimeRes.error) {
          setCleanerError(true);
        }
      },
      time: 500,
      call: true
    },
    [availableTimeRes.error]
  );

  useEffect(() => {
    if (!availableTimeRes.error) {
      setCleanerError(false);
    }
  }, [availableTimeRes.error]);

  useEffect(() => {
    const { disabled_days } = disabledDatesRes.data || {};
    const param_date = disabledDatesRes.params.date;

    if (param_date && month && param_date.getMonth() === month.getMonth()) {
      setDisabledDateMonth(disabled_days ? month.getMonth() : undefined);
      setDisabledDates(disabled_days || undefined);
    }
  }, [disabledDatesRes.data, disabledDatesRes.params.date, month]);

  useEffect(() => {
    if (disabledDates) {
      /*
       * If the currently selected date is disabled,
       * remove the date from orderDetails
       *
       * Then make the nearest available date selected
       *
       * If the end of the month is reach, make requests that:
       * - Gets the disabled days of the next month,
       * - Iterate to the first accessible day of the next month
       * - Set the first accessible day found
       * - The cleaner list query and orderDetails will respond automatically
       * Clearing them manually may also be necessary
       */
      const hydratedDate = extractTimeStartDetails(orderDetailsRef.current.time_start, checkout).date;
      const date_value = date || hydratedDate || month || new Date();

      const disabledMonth = moment(month).format("YYYY-MM");
      const orderMonth = moment(date_value).format("YYYY-MM");
      const orderDate = moment(date_value).format("YYYY-MM-DD");

      if (disabledMonth === orderMonth) {
        if (checkout) {
          const isDateDisabled =
            !(date || hydratedDate) ||
            disabledDates.some(
              (d) => `${disabledMonth}-${prefixTransform(d)}` === orderDate
            );

          if (isDateDisabled && monthMovementCount <= 3) {
            // Only move a maximum of 3 months away
            // Autoselect
            const { newDate, newMonth } = getNextAvailableDay({
              disabledDates,
              presentDayDate: date_value
            });

            if (newDate) {
              setDate(newDate);
            } else if (newMonth) {
              setMonthMovementCount(monthMovementCount + 1);
              setMonth(newMonth);
            }

            setCleaners(undefined);
            setOrderDetails({
              time_start: null
            });
          }
        }
      }
    }
  }, [disabledDates]);

  useEffect(() => {
    if (checkout) {
      const cleaners = addressBlackListCheckRes.data || [];
      const loading = addressBlackListCheckRes.loading;
      const error = addressBlackListCheckRes.error;
      const addressFetchDone =
        addressBlackListCheckRes.called && !addressBlackListCheckRes.loading;

      const effectiveDateForCleaners = date || extractTimeStartDetails(orderDetailsRef.current.time_start, checkout).date;
      const sameDate = sameDates(effectiveDateForCleaners, addressBlackListCheckRes.params.date);
      const sameLocation =
        longitude === addressBlackListCheckRes.params.longitude &&
        latitude === addressBlackListCheckRes.params.latitude;
      const hasSameParams = sameDate && sameLocation;

      if (hasSameParams) setCleaners([...cleaners]);
      if (
        checkout &&
        addressFetchDone &&
        addressBlackListCheckRes.params.address &&
        addressBlackListCheckRes.params.date &&
        String(addressBlackListCheckRes.params.longitude) &&
        String(addressBlackListCheckRes.params.latitude) &&
        !cleaners.length
      ) {
        Sentry.captureMessage("No cleaners for active day", {
          level: "info",
          extra: {
            ...addressBlackListCheckRes.params
          }
        });
      }

      // Check if the cleaner_id in orderDetails is authentic
      let isExisiting = cleaners.some(
        (cleaner) => cleaner.id === orderDetailsRef.current.cleaner_id
      );

      // Use address || orderDetails.client_address to avoid wiping time_start
      // during hydration when the local address state is briefly empty
      const effectiveAddress = address || orderDetailsRef.current.client_address;

      if (!loading && !error) {
        setOrderDetails({
          client_address: address || null,
          longitude: longitude || null,
          latitude: latitude || null,
          eircode: eircode || null,
          ...(!effectiveAddress
            ? {
                time_start: null
              }
            : {}),
          ...(!isExisiting
            ? {
                cleaner_id: null,
                random_cleaner: true
              }
            : {})
        });
      }
    }
  }, [addressBlackListCheckRes.data]);

  useEffect(() => {
    if (checkout) {
      if (Boolean(addressBlackListCheckRes.error) && !hide) {
        setOrderDetails({
          client_address: null,
          longitude: null,
          latitude: null,
          eircode: null
        });
        if (addressBlackListCheckRes.errorObject) {
          const type = (addressBlackListCheckRes.errorObject as { type: any })
            .type;
          if (type === "no_free_time") {
            setTimeDetailsError(true);
          } else if (type === "not_valid_location") {
            setNoCleanerLocationError(true);
          } else {
            setAddressBlacklisted(true);
          }
        } else {
          setAddressBlacklisted(true);
        }
      } else if (addressBlackListCheckRes.called) {
        setTimeDetailsError(false);
        setNoCleanerLocationError(false);
        setAddressBlacklisted(false);
      }
    }
  }, [
    addressBlackListCheckRes.called,
    addressBlackListCheckRes.error,
    addressBlackListCheckRes.errorObject,
    checkout,
    hide
  ]);

  const effectiveDateReturn = date || extractTimeStartDetails(orderDetails.time_start, checkout).date;

  return {
    serviceDuration,
    cleanerError,
    date: effectiveDateReturn,
    setDate,
    month,
    setMonth,
    cleaners,
    timeArray,
    addressBlackListCheckRes,
    disabledDatesRes,
    availableTimeRes,
    cleanerInterval: timeArray,
    disabledDateObj: { month: disabledDateMonth, data: disabledDates },
    timeDetailsError: timeDetailsError
      ? t(
          "Your booking would take longer than any business day of our cleaners. Please remove some services and check the calendar again."
        )
      : undefined,
    noCleanerLocationError: noCleanerLocationError
      ? "No cleaner for selected services in given location"
      : undefined
  };
};
